/**
 * The profile screen: the learner's own record, and the builder they make
 * their figure in (components/profile/Profile.tsx, components/avatar/).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## The part names are here, not in the data
 *
 * `web/src/lib/avatar.ts` is the design system as it was delivered, and it
 * ships an English word with every part — "Buzz", "Hijab", "Hi-vis". Those are
 * the one thing in that file a learner actually reads, and a builder that
 * offers `تمرين سريع` beside `Ponytail` is a screen that has been translated
 * everywhere except where somebody is looking. So the data keeps its keys and
 * the words live here, under `parts`, keyed by rail and then by part key.
 *
 * `tsc` cannot check that pairing — the keys are strings in a data file, not a
 * type — so web/test/avatar.test.ts does: every part in the catalogue has a
 * name in both languages, and no name here belongs to a part that no longer
 * exists. A part added upstream without a word in this file fails that test
 * rather than appearing in front of an Arabic reader in English.
 *
 * The rail names are the same arrangement one level up, under `rails`.
 *
 * Numbers are formatted through lib/format with an explicit locale rather
 * than the ambient one, because a catalogue is a value: `stringsFor('ar')`
 * has to build Arabic sentences whether or not the page it is being read on
 * is currently Arabic.
 */

import { num } from '../format';
import { characterWords as cw } from './avatar-character';


export const en = {
  /* The link into it, wherever it is offered. */
  open: 'Your profile',
  /* The screen. */
  title: 'Your profile',
  back: 'Level list',
  /* Shown where the name goes for somebody who has never typed one. The
     protocol falls back to the same word for an account with no usable name. */
  noName: 'Learner',
  /* Said under the name when nobody is signed in. The record is real; where it lives is not a promise. */
  localOnly: 'This browser',
  localOnlyWhy: 'Your record and your figure are kept in this browser. Sign in and both follow you to any other.',
  signIn: 'Sign in',
  signingIn: 'Signing in…',
  /**
   * The page other people can open (components/profile/PublicProfile.tsx).
   *
   * Offered here because this screen is where a learner comes to look at
   * themselves, and because the link is otherwise unfindable: it is addressed
   * by a tag, and nothing else in the product would ever show somebody their
   * own.
   *
   * `what` is the whole disclosure and is deliberately two facts rather than
   * one reassurance: anybody who has the link can open it, and the only thing
   * on it is the part a server checked. A learner should be able to decide
   * whether to send it from that sentence alone.
   */
  /**
   * The teams this learner is in (worker/teams.ts, docs/teams.md).
   *
   * On this screen because a record of what somebody has done is the obvious
   * place to say who they are doing it with, and because the list was
   * otherwise only inside the chat panel — two clicks away, behind a bubble,
   * with nothing on the page saying it was there.
   *
   * What this block does **not** do is manage anything. Creating a team,
   * inviting, leaving and answering an invitation all stay in the chat panel,
   * which is where they already work and where their refusals are already
   * worded. A second surface doing the same writes is a second place for the
   * invitation state to be a moment out of date.
   */
  teams: {
    label: 'Your teams',
    /** Signed out: a team belongs to an account, so there is nothing to list. */
    localOnly: 'Teams are kept with your account. Sign in to make one.',
    none: 'You are in no teams yet.',
    /* Where a team comes from, said where the list would be. There is no
       button here: the one way to ask somebody is from their card in a room,
       because an invitation travels by tag and a name you can click is the
       only place a tag comes from. */
    noneWhy: 'Open the chat panel to make one. You invite people from their card in a room.',
    /** The one action here, and it is a way in rather than a change. */
    open: 'Open the room',
    /* Invitations are answered in the chat panel, so this points at it rather
       than listing them. Deliberately without a number: the panel's own Teams
       button already carries the count, and a second count on another screen
       is a second thing that can be a moment out of date. The headcount and
       the word beside a lead are not here either — the component reads
       `chat.teams.size` and `chat.teams.lead`, so the roster on this page and
       the roster in the panel say the same words about the same people. */
    waiting: 'You have invitations waiting in the chat panel.',
  },

  publicPage: {
    label: 'Your public page',
    what: 'Anyone with this link can open it. It shows the completions a server checked — never the record your browser keeps.',
    open: 'Open it',
    copy: 'Copy link',
    copied: 'Link copied',
    /* No account, so there is no page: the record lives in this browser and
       has no address anybody else could ask for. */
    localOnly: 'Sign in to have a page other people can open.',
  },

  /**
   * The name, and changing it.
   *
   * The one thing on this screen that other people read. It was settled
   * everywhere else and nowhere on purpose: signed in, it came from Google and
   * could only be changed at Google; signed out, it was picked in the chat
   * panel, behind a bubble, at the moment somebody first wanted to say
   * something. Neither is a place you would go looking for it.
   *
   * `rule` is not here. It is the protocol's own sentence, asked for through
   * `chat.rules.name()`, because the server refuses with the same rule and a
   * second wording of it in this file would be a second thing to keep true.
   */
  name: {
    label: 'Your name',
    edit: 'Change name',
    close: 'Cancel',
    field: 'Name',
    /* Under the field before anything has been refused: what it is for, and how far it reaches. */
    note: 'This is what a room shows beside your figure, and what is on the page other people can open.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved. Every room you are in has it now.',
    /* Signed in, so the write went to the account and the account is what the rooms read. */
    failed: 'That could not be saved to your account. Your name has not changed. Try again in a moment.',
    /* Signed in only: the account name is still there underneath, so there is always a way back. */
    google: 'Use my Google name',
    googleWhy: 'Your account name stays underneath. Signing in again never takes the one you chose.',
    /* Signed in with an emailed code: there is no other name underneath. */
    keptWhy: 'Signing in again never takes the name you chose.',
    /* Signed out: the name is this browser's, the way the record is. */
    localOnly: 'This name is kept in this browser. Sign in and it follows you to any other.',
  },

  /* The figure, and what it is. */
  operator: {
    label: 'Your figure',
    /* Two sentences under the big figure. The second is the point: it is not a photograph of anybody. */
    what: 'Nine numbers, drawn in your browser at sixteen pixels square.',
    where: 'It is what a room shows beside your name.',
    seed: 'Seed',
    seedWhy: 'The whole avatar. Nothing is uploaded.',
    /* The state before anybody has built one. */
    derived: 'Made from your id',
    derivedWhy: 'Everybody starts as somebody. Build your own whenever you like.',
    edit: 'Change it',
    close: 'Done',
    /* Shown while a signed-in learner wears the picture from their Google account. */
    picture: 'Your account picture',
    pictureWhy: 'Build a figure and it replaces the picture everywhere in the chat.',
  },

  /* The builder. */
  build: {
    title: 'Build your figure',
    preview: 'Preview',
    /* The three sizes the preview shows, so a learner sees the size a room will. */
    atSize: (px: number) => `${num(px)} px`,
    random: 'Surprise me',
    reset: 'Back to the one my id gives me',
    save: 'Wear it',
    saving: 'Saving…',
    cancel: 'Cancel',
    unsaved: 'Not saved yet',
    saved: 'Saved. Every room you are in has it now.',
    failed: 'That could not be saved to your account. It is on this browser, and the next save will try again.',
    /* One rail's grid, for a screen reader. */
    railLabel: (rail: string) => `${rail}, pick one`,
    /* On a locked tile, and in its title. */
    locked: (level: number) => `Clear ${num(level)} ${level === 1 ? 'level' : 'levels'}`,
    lockedShort: 'Locked',
    /* Said once above the rails, so the locks are not a surprise. */
    locksWhy: 'A few parts open as you clear levels. Nothing that makes you look like a person is ever locked.',
    /* The count under the rails: what is open out of what exists. */
    openParts: (open: number, all: number) => `${num(open)} of ${num(all)} parts open`,
  },

  /* The standing block: the same numbers the level list shows, and the ones it has no room for. */
  stats: {
    label: 'Your record',
    rank: 'Rank',
    rankMeter: 'Progress to the next rank',
    toNextRank: (xp: number, rank: string) => `${num(xp)} XP to ${rank}`,
    topRank: 'The top of the ladder.',
    xp: 'XP',
    levels: 'Levels',
    stars: 'Stars',
    /* The second row: the things a learner cannot read off the map. */
    perfect: 'Three-star levels',
    noHints: 'Cleared with no hints',
    hints: 'Hints taken',
    attempts: 'Attempts',
    unverified: 'Unverified clears',
    unverifiedWhy: 'A clear the grader could not vouch for. Play it again cleanly and the star fills in.',
    skipped: 'Levels skipped',
    skippedWhy: 'Moved past without clearing. They are still there whenever you want them.',
    started: 'First clear',
    latest: 'Last clear',
    /* Before anything has been cleared. */
    nothingYet: 'Nothing cleared yet. The record starts at Level 00.',
    start: 'Start Level 00',
  },

  /* The per-chapter table. */
  worlds: {
    label: 'By chapter',
    chapter: 'Chapter',
    cleared: 'Cleared',
    stars: 'Stars',
    /* A chapter nobody has reached yet. */
    locked: 'Not reached',
  },


  /**
   * Closing the account (worker/account.ts, `deleteAccount`).
   *
   * At the foot of the screen, after everything it destroys has been shown,
   * which is the only honest place for it: a learner should reach this having
   * just scrolled past the record they are about to give up.
   *
   * `goes` and `stays` are the whole of the confirmation and are two lists
   * rather than one paragraph, because a paragraph about what a delete does is
   * a paragraph nobody finishes. Every line in `stays` is something a learner
   * could reasonably have assumed was covered — the chat especially — and
   * saying so here is what makes this screen the same statement the privacy
   * policy makes. Neither list is decoration: if what the server does changes,
   * these change in the same commit.
   *
   * `phrase` is typed out before the button works. It is in the catalogue and
   * not in the Worker on purpose: what it guards against is a person pressing
   * something they did not mean to, which is this screen's job, and a server
   * grading a translated word would be a security boundary with a translation
   * in it (worker/entitlement.ts's header argues the same thing about `?lang=`).
   */
  deletion: {
    label: 'Closing your account',
    /* Said before anything is opened. Two facts and no reassurance. */
    what: 'Deleting your account removes it from the server with everything saved to it. It cannot be undone.',
    /* Signed out: there is no account here, and the record is this browser's. */
    localOnly: 'There is no account to delete. Your record is kept in this browser.',
    open: 'Delete your account',
    cancel: 'Keep my account',

    /* The confirmation, which quotes what is about to happen. */
    title: 'Delete your account?',
    goesLabel: 'What is deleted',
    goes: [
      'Your progress on the server — levels, stars and XP.',
      'Every completion a server checked, and the work you uploaded to have it checked.',
      'Your name, your figure, and the page other people can open.',
      'Any picture you posted in the chat.',
      'Your teams. A team you started is disbanded and its room is deleted with it.',
      'Your paid-task claims that did not pass, and what you delivered for them.',
      'AI credits from tasks that you have not spent: they go with the account and cannot be used or moved anywhere else.',
    ],
    staysLabel: 'What stays',
    stays: [
      'What you wrote in chat rooms. The messages stay where you posted them; the tag beside them stops pointing at anybody, and nothing left can say they were yours.',
      'The record in this browser. Your levels stay here, signed out, exactly as they are.',
      'A single-use invite code you spent stays spent.',
      'Work you delivered for a paid task that passed, which is ours under the terms, and the record of the AI credits it earned, unlinked from you.',
    ],
    again: 'Signing in again afterwards, with Google or the same email, starts a new, empty one.',

    /* The typed confirmation, and the press it unlocks. */
    field: (phrase: string) => `Type “${phrase}” to confirm`,
    phrase: 'delete',
    confirm: 'Delete my account',
    deleting: 'Deleting…',

    /**
     * The second press, and only for an account that has bought the course.
     *
     * The server refuses the first request for exactly this reason rather than
     * trusting the screen to have said it, so this sentence cannot be skipped
     * by a tab that is out of date — it will simply be refused until it shows
     * it.
     */
    entitled: 'This account has paid for something still running: time on the course. Deleting it gives that up: there is no refund, and no way to get it back.',
    entitledConfirm: 'Delete it anyway',

    failed: 'That could not be done. Your account has not changed. Try again in a moment.',
    /* Raised from state/useAccount, outside the render tree. */
    toastGone: 'Your account is deleted. This browser keeps the record it already had.',
  },

  /* The nine rails, in the order the builder offers them. */
  rails: {
    build: 'Build',
    kit: 'Kit',
    hair: 'Head',
    eyes: 'Eyes',
    face: 'Face',
    mouth: 'Mouth',
    acc: 'Accessory',
    tone: 'Ink tone',
    plate: 'Frame',
    ...cw.en.rails,
  },

  /**
   * The parts, by rail and then by the key in web/src/lib/avatar.ts.
   *
   * Typed as a plain index rather than as nine exact shapes on purpose: the
   * catalogue is data, the keys are strings, and the pairing is checked by
   * web/test/avatar.test.ts where it can be checked properly — in both
   * directions, against the catalogue that actually ships.
   */
  parts: {
    ...cw.en.parts,
    build: { standard: 'Standard', narrow: 'Narrow', broad: 'Broad' },
    kit: {
      ...cw.en.parts.kit,
      hoodie: 'Hoodie',
      tee: 'Tee',
      shirt: 'Shirt',
      jacket: 'Jacket',
      lanyard: 'Lanyard',
      turtleneck: 'Turtleneck',
      flannel: 'Flannel',
      vest: 'Vest',
      overalls: 'Overalls',
      hivis: 'Hi-vis',
      tank: 'Tank',
      blouse: 'Blouse',
      wraptop: 'Wrap top',
      scarf: 'Scarf',
    },
    hair: {
      shaved: 'Shaved',
      buzz: 'Buzz',
      crop: 'Crop',
      curtain: 'Curtain',
      beanie: 'Beanie',
      cap: 'Cap',
      volume: 'Volume',
      bun: 'Bun',
      tail: 'Ponytail',
      mohawk: 'Mohawk',
      hood: 'Hood up',
      bob: 'Bob',
      long: 'Long',
      braids: 'Braids',
      puffs: 'Puffs',
      pixie: 'Pixie',
      locs: 'Locs',
      wrap: 'Headwrap',
      hijab: 'Hijab',
    },
    eyes: {
      scan: 'Scan',
      dot: 'Dot',
      dim: 'Dim',
      wink: 'Wink',
      visor: 'Visor',
      goggles: 'Goggles',
      wide: 'Wide',
      closed: 'Closed',
      shades: 'Shades',
      target: 'Targeting',
    },
    face: {
      none: 'None',
      stubble: 'Stubble',
      beard: 'Beard',
      freckles: 'Freckles',
      scar: 'Scar',
      lashes: 'Lashes',
      brows: 'Brows',
      mark: 'Forehead mark',
    },
    mouth: { line: 'Line', flat: 'Flat', smirk: 'Smirk', grin: 'Grin', open: 'Open', tight: 'Tight', none: 'None' },
    acc: {
      none: 'None',
      glasses: 'Glasses',
      stud: 'Stud',
      cans: 'Headphones',
      band: 'Headband',
      wire: 'Earbud',
      patch: 'Eye patch',
      pin: 'Shoulder pin',
      hoops: 'Hoops',
      drops: 'Drop earrings',
      chain: 'Chain',
      clip: 'Hair clip',
    },
    tone: { ...cw.en.parts.tone, standard: 'Standard', bright: 'Bright', soft: 'Soft', deep: 'Deep' },
    plate: { hairline: 'Hairline', corners: 'Corners', dots: 'Dot grid', lines: 'Scanline', lift: 'Power up', nod: 'Ready nod', spark: 'Signal burst' },
  } as Record<string, Record<string, string>>,
};

export const ar: typeof en = {
  open: 'ملفك',
  title: 'ملفك',
  back: 'قائمة المستويات',
  noName: 'متعلّم',
  localOnly: 'هذا المتصفح',
  localOnlyWhy: 'سجلك وشخصيتك محفوظان في هذا المتصفح. سجّل الدخول ليتبعاك إلى أي متصفح آخر.',
  signIn: 'تسجيل الدخول',
  signingIn: 'جارٍ تسجيل الدخول…',
  teams: {
    label: 'فرقك',
    localOnly: 'الفرق محفوظة مع حسابك. سجّل الدخول لتنشئ فريقًا.',
    none: 'لست في أي فريق بعد.',
    noneWhy: 'افتح لوحة المحادثة لتنشئ فريقًا، وتدعو الناس من بطاقاتهم داخل الغرفة.',
    open: 'افتح الغرفة',
    waiting: 'لديك دعوات تنتظر ردّك في لوحة المحادثة.',
  },

  publicPage: {
    label: 'صفحتك العامة',
    what: 'يستطيع فتحها كل من يملك هذا الرابط. تعرض الإنجازات التي فحصها خادم، ولا تعرض السجلّ المحفوظ في متصفحك.',
    open: 'افتحها',
    copy: 'انسخ الرابط',
    copied: 'نُسخ الرابط',
    localOnly: 'سجّل الدخول لتصبح لك صفحة يفتحها الآخرون.',
  },

  name: {
    label: 'اسمك',
    edit: 'غيّر الاسم',
    close: 'إلغاء',
    field: 'الاسم',
    note: 'هذا ما تعرضه الغرفة بجانب شخصيتك، وما يظهر في الصفحة التي يستطيع الآخرون فتحها.',
    save: 'احفظ',
    saving: 'جارٍ الحفظ…',
    saved: 'حُفظ. كل غرفة أنت فيها تعرضه الآن.',
    failed: 'تعذّر حفظه في حسابك. اسمك لم يتغيّر. أعد المحاولة بعد قليل.',
    google: 'استخدم اسمي في جوجل',
    googleWhy: 'اسم حسابك يبقى تحته. تسجيل الدخول مرة أخرى لا يأخذ منك الاسم الذي اخترته.',
    keptWhy: 'تسجيل الدخول مرة أخرى لا يأخذ منك الاسم الذي اخترته.',
    localOnly: 'هذا الاسم محفوظ في هذا المتصفح. سجّل الدخول ليتبعك إلى أي متصفح آخر.',
  },

  operator: {
    label: 'شخصيتك',
    what: 'تسعة أرقام، يرسمها متصفحك في مربع من 16×16 بكسل.',
    where: 'هذا ما تعرضه الغرفة بجانب اسمك.',
    seed: 'البذرة',
    seedWhy: 'الصورة كلها. لا شيء يُرفع إلى أي خادم.',
    derived: 'مشتقة من معرّفك',
    derivedWhy: 'كل واحد يبدأ بشخصية. ابنِ شخصيتك متى شئت.',
    edit: 'غيّرها',
    close: 'تم',
    picture: 'صورة حسابك',
    pictureWhy: 'ابنِ شخصيتك وستحل محل الصورة في كل مكان داخل المحادثة.',
  },

  build: {
    title: 'ابنِ شخصيتك',
    preview: 'معاينة',
    atSize: (px: number) => `${num(px, 'ar')} بكسل`,
    random: 'فاجئني',
    reset: 'أعد الشخصية المشتقة من معرّفي',
    save: 'ارتدِها',
    saving: 'جارٍ الحفظ…',
    cancel: 'إلغاء',
    unsaved: 'لم تُحفظ بعد',
    saved: 'حُفظت. كل غرفة أنت فيها تعرضها الآن.',
    failed: 'تعذّر حفظها في حسابك. هي محفوظة في هذا المتصفح، وستُحاول المحاولة التالية مرة أخرى.',
    railLabel: (rail: string) => `${rail}، اختر واحدًا`,
    locked: (level: number) => level === 1 ? 'أنهِ مستوى واحدًا' : level === 2 ? 'أنهِ مستويين' : `أنهِ ${num(level, 'ar')} ${level > 10 ? 'مستوى' : 'مستويات'}`,
    lockedShort: 'مقفل',
    locksWhy: 'بعض القطع تُفتح كلما أنهيت مستويات. ولا شيء مما يجعلك تبدو إنسانًا مقفل أبدًا.',
    openParts: (open: number, all: number) => `${num(open, 'ar')} من ${num(all, 'ar')} قطعة مفتوحة`,
  },

  stats: {
    label: 'سجلك',
    rank: 'الرتبة',
    rankMeter: 'التقدم نحو الرتبة التالية',
    toNextRank: (xp: number, rank: string) => `${num(xp, 'ar')} نقطة خبرة حتى ${rank}`,
    topRank: 'أعلى السُّلّم.',
    xp: 'نقاط الخبرة',
    levels: 'المستويات',
    stars: 'النجوم',
    perfect: 'مستويات بثلاث نجوم',
    noHints: 'أُنهيت دون تلميحات',
    hints: 'تلميحات مأخوذة',
    attempts: 'المحاولات',
    unverified: 'إنجازات غير متحقَّق منها',
    unverifiedWhy: 'إنجاز لم يستطع المصحّح أن يشهد له. أعد اللعب بنظافة وتمتلئ النجمة.',
    skipped: 'مستويات متخطّاة',
    skippedWhy: 'تجاوزتها دون إنهائها. ما زالت هناك متى أردتها.',
    started: 'أول إنجاز',
    latest: 'آخر إنجاز',
    nothingYet: 'لا شيء منجز بعد. السجل يبدأ من المستوى 00.',
    start: 'ابدأ المستوى 00',
  },

  worlds: {
    label: 'حسب الفصل',
    chapter: 'الفصل',
    cleared: 'المنجزة',
    stars: 'النجوم',
    locked: 'لم تصل إليه',
  },


  deletion: {
    label: 'إغلاق حسابك',
    what: 'حذف حسابك يزيله من الخادم بكل ما هو محفوظ فيه. ولا رجعة في ذلك.',
    localOnly: 'لا يوجد حساب لتحذفه. سجلك محفوظ في هذا المتصفح.',
    open: 'احذف حسابك',
    cancel: 'أبقِ حسابي',

    title: 'هل تحذف حسابك؟',
    goesLabel: 'ما الذي يُحذف',
    goes: [
      'تقدمك المحفوظ على الخادم: المستويات والنجوم ونقاط الخبرة.',
      'كل إنجاز فحصه خادم، والعمل الذي رفعته ليُفحص.',
      'اسمك وشخصيتك والصفحة التي يفتحها الآخرون.',
      'أي صورة نشرتها في المحادثة.',
      'فرقك. والفريق الذي أنشأته يُحلّ وتُحذف غرفته معه.',
      'حجوزاتك للمهام المدفوعة التي لم تنجح، وما سلّمته لها.',
      'رصيدك للذكاء الاصطناعي من المهام الذي لم تنفقه: يذهب مع الحساب، ولا يمكن استخدامه أو نقله إلى أي مكان آخر.',
    ],
    staysLabel: 'ما الذي يبقى',
    stays: [
      'ما كتبته في غرف المحادثة. الرسائل تبقى حيث نشرتها، والوسم بجانبها لا يدل على أحد بعد ذلك، ولا يبقى ما يقول إنها لك.',
      'السجل المحفوظ في هذا المتصفح. مستوياتك تبقى هنا كما هي وأنت خارج الحساب.',
      'رمز الدعوة الذي أنفقته يبقى منفقًا.',
      'العمل الذي سلّمته لمهمة مدفوعة نجحت، وهو ملكنا بموجب الشروط، وسجل رصيد الذكاء الاصطناعي الذي كسبه، مفصولًا عنك.',
    ],
    again: 'تسجيل الدخول بعد ذلك، عبر Google أو بالبريد نفسه، يبدأ حسابًا جديدًا فارغًا.',

    field: (phrase: string) => `اكتب «${phrase}» للتأكيد`,
    phrase: 'احذف',
    confirm: 'احذف حسابي',
    deleting: 'جارٍ الحذف…',

    entitled: 'هذا الحساب دفع مقابل شيء ما زال سارياً: مدة من الدورة. حذفه يتخلى عن ذلك: لا استرداد للمبلغ، ولا طريق للعودة.',
    entitledConfirm: 'احذفه على أي حال',

    failed: 'تعذّر تنفيذ ذلك. حسابك لم يتغيّر. أعد المحاولة بعد قليل.',
    toastGone: 'حُذف حسابك. هذا المتصفح يحتفظ بالسجل الذي لديه.',
  },

  rails: {
    build: 'البنية',
    kit: 'الملابس',
    hair: 'الرأس',
    eyes: 'العينان',
    face: 'الوجه',
    mouth: 'الفم',
    acc: 'إكسسوار',
    tone: 'درجة الحبر',
    plate: 'الإطار',
    ...cw.ar.rails,
  },

  parts: {
    ...cw.ar.parts,
    build: { standard: 'قياسية', narrow: 'نحيلة', broad: 'عريضة' },
    kit: {
      ...cw.ar.parts.kit,
      hoodie: 'هودي',
      tee: 'تي شيرت',
      shirt: 'قميص',
      jacket: 'جاكيت',
      lanyard: 'بطاقة معلّقة',
      turtleneck: 'ياقة عالية',
      flannel: 'قميص فانيلا',
      vest: 'صديري',
      overalls: 'أفرول',
      hivis: 'سترة عاكسة',
      tank: 'قميص بحمالات',
      blouse: 'بلوزة',
      wraptop: 'بلوزة ملفوفة',
      scarf: 'وشاح',
    },
    hair: {
      shaved: 'حليق',
      buzz: 'قصير جدًا',
      crop: 'قصير',
      curtain: 'مفروق',
      beanie: 'طاقية',
      cap: 'كاب',
      volume: 'كثيف',
      bun: 'كعكة',
      tail: 'ذيل حصان',
      mohawk: 'موهوك',
      hood: 'قلنسوة مرفوعة',
      bob: 'بوب',
      long: 'طويل',
      braids: 'ضفائر',
      puffs: 'كرتان',
      pixie: 'بيكسي',
      locs: 'جدائل ملتفة',
      wrap: 'لفّة رأس',
      hijab: 'حجاب',
    },
    eyes: {
      scan: 'ماسحة',
      dot: 'نقطتان',
      dim: 'خافتة',
      wink: 'غمزة',
      visor: 'واقٍ',
      goggles: 'نظارة واقية',
      wide: 'واسعة',
      closed: 'مغلقة',
      shades: 'نظارة شمس',
      target: 'تصويب',
    },
    face: {
      none: 'بلا',
      stubble: 'ذقن خفيفة',
      beard: 'لحية',
      freckles: 'نمش',
      scar: 'ندبة',
      lashes: 'رموش',
      brows: 'حاجبان',
      mark: 'علامة على الجبين',
    },
    mouth: { line: 'خط', flat: 'مستوٍ', smirk: 'ابتسامة جانبية', grin: 'ابتسامة عريضة', open: 'مفتوح', tight: 'مطبق', none: 'بلا' },
    acc: {
      none: 'بلا',
      glasses: 'نظارة',
      stud: 'قرط صغير',
      cans: 'سماعات رأس',
      band: 'عصابة رأس',
      wire: 'سماعة أذن',
      patch: 'رقعة عين',
      pin: 'دبوس كتف',
      hoops: 'حلقتان',
      drops: 'أقراط متدلية',
      chain: 'سلسلة',
      clip: 'مشبك شعر',
    },
    tone: { ...cw.ar.parts.tone, standard: 'قياسية', bright: 'ساطعة', soft: 'هادئة', deep: 'غامقة' },
    plate: { hairline: 'خط رفيع', corners: 'زوايا', dots: 'شبكة نقاط', lines: 'خطوط مسح', lift: 'انطلاقة', nod: 'إيماءة استعداد', spark: 'شرارة إشارة' },
  },
};

export const de: typeof en = {
  open: 'Dein Profil',
  title: 'Dein Profil',
  back: 'Levelliste',
  noName: 'Lernende Person',
  localOnly: 'Dieser Browser',
  localOnlyWhy: 'Dein Stand und deine Figur werden in diesem Browser gespeichert. Melde dich an, dann folgen dir beide in jeden anderen.',
  signIn: 'Anmelden',
  signingIn: 'Anmeldung läuft …',
  teams: {
    label: 'Deine Teams',
    localOnly: 'Teams werden mit deinem Konto gespeichert. Melde dich an, um eins zu gründen.',
    none: 'Du bist noch in keinem Team.',
    noneWhy: 'Öffne das Chat-Panel, um eins zu gründen. Leute lädst du über ihre Karte in einem Raum ein.',
    open: 'Raum öffnen',
    waiting: 'Im Chat-Panel warten Einladungen auf dich.',
  },
  publicPage: {
    label: 'Deine öffentliche Seite',
    what: 'Jeder mit diesem Link kann sie öffnen. Sie zeigt die Abschlüsse, die ein Server geprüft hat – nie den Stand, den dein Browser speichert.',
    open: 'Öffnen',
    copy: 'Link kopieren',
    copied: 'Link kopiert',
    localOnly: 'Melde dich an, um eine Seite zu haben, die andere öffnen können.',
  },
  name: {
    label: 'Dein Name',
    edit: 'Namen ändern',
    close: 'Abbrechen',
    field: 'Name',
    note: 'So zeigt dich ein Raum neben deiner Figur, und so steht es auf der Seite, die andere öffnen können.',
    save: 'Speichern',
    saving: 'Wird gespeichert …',
    saved: 'Gespeichert. Jeder Raum, in dem du bist, hat ihn jetzt.',
    failed: 'Das konnte nicht in deinem Konto gespeichert werden. Dein Name hat sich nicht geändert. Versuch es gleich noch einmal.',
    google: 'Meinen Google-Namen verwenden',
    googleWhy: 'Dein Kontoname bleibt darunter erhalten. Eine erneute Anmeldung nimmt dir nie den Namen, den du gewählt hast.',
    keptWhy: 'Eine erneute Anmeldung nimmt dir nie den Namen, den du gewählt hast.',
    localOnly: 'Dieser Name wird in diesem Browser gespeichert. Melde dich an, dann folgt er dir in jeden anderen.',
  },
  operator: {
    label: 'Deine Figur',
    what: 'Neun Zahlen, in deinem Browser gezeichnet, sechzehn Pixel im Quadrat.',
    where: 'Sie zeigt ein Raum neben deinem Namen.',
    seed: 'Seed',
    seedWhy: 'Der ganze Avatar. Nichts wird hochgeladen.',
    derived: 'Aus deiner ID erzeugt',
    derivedWhy: 'Jede und jeder fängt als irgendwer an. Bau dir deine eigene, wann immer du willst.',
    edit: 'Ändern',
    close: 'Fertig',
    picture: 'Dein Kontobild',
    pictureWhy: 'Bau dir eine Figur, dann ersetzt sie das Bild überall im Chat.',
  },
  build: {
    title: 'Bau deine Figur',
    preview: 'Vorschau',
    atSize: (px) => `${num(px, 'de')} px`,
    random: 'Überrasch mich',
    reset: 'Zurück zu dem, den meine ID ergibt',
    save: 'Übernehmen',
    saving: 'Wird gespeichert …',
    cancel: 'Abbrechen',
    unsaved: 'Noch nicht gespeichert',
    saved: 'Gespeichert. Jeder Raum, in dem du bist, hat ihn jetzt.',
    failed: 'Das konnte nicht in deinem Konto gespeichert werden. Es liegt in diesem Browser, und beim nächsten Speichern wird es erneut versucht.',
    railLabel: (rail) => `${rail}, wähl eins`,
    locked: (level) => `${num(level, 'de')} Level schaffen`,
    lockedShort: 'Gesperrt',
    locksWhy: 'Ein paar Teile öffnen sich, wenn du Level schaffst. Nichts, womit du wie ein Mensch aussiehst, ist je gesperrt.',
    openParts: (open, all) => `${num(open, 'de')} von ${num(all, 'de')} Teilen offen`,
  },
  stats: {
    label: 'Dein Stand',
    rank: 'Rang',
    rankMeter: 'Fortschritt zum nächsten Rang',
    toNextRank: (xp, rank) => `${num(xp, 'de')} XP bis ${rank}`,
    topRank: 'Die Spitze der Leiter.',
    xp: 'XP',
    levels: 'Level',
    stars: 'Sterne',
    perfect: 'Level mit drei Sternen',
    noHints: 'Ohne Hinweise geschafft',
    hints: 'Genommene Hinweise',
    attempts: 'Versuche',
    unverified: 'Unbestätigte Abschlüsse',
    unverifiedWhy: 'Ein Abschluss, für den die Bewertung nicht bürgen konnte. Spiel es noch einmal sauber, dann füllt sich der Stern.',
    skipped: 'Übersprungene Level',
    skippedWhy: 'Übersprungen, ohne sie zu schaffen. Sie sind noch da, wann immer du sie willst.',
    started: 'Erster Abschluss',
    latest: 'Letzter Abschluss',
    nothingYet: 'Noch nichts geschafft. Der Stand beginnt bei Level 00.',
    start: 'Level 00 starten',
  },
  worlds: {
    label: 'Nach Kapitel',
    chapter: 'Kapitel',
    cleared: 'Geschafft',
    stars: 'Sterne',
    locked: 'Nicht erreicht',
  },

  deletion: {
    label: 'Dein Konto schließen',
    what: 'Wenn du dein Konto löschst, wird es mit allem, was darin gespeichert ist, vom Server entfernt. Das lässt sich nicht rückgängig machen.',
    localOnly: 'Es gibt kein Konto zu löschen. Dein Stand wird in diesem Browser gespeichert.',
    open: 'Konto löschen',
    cancel: 'Konto behalten',
    title: 'Dein Konto löschen?',
    goesLabel: 'Was gelöscht wird',
    goes: [
      'Dein Fortschritt auf dem Server – Level, Sterne und XP.',
      'Jeder Abschluss, den ein Server geprüft hat, und die Arbeit, die du dafür hochgeladen hast.',
      'Dein Name, deine Figur und die Seite, die andere öffnen können.',
      'Jedes Bild, das du im Chat gepostet hast.',
      'Deine Teams. Ein Team, das du gegründet hast, wird aufgelöst, und sein Raum wird mit gelöscht.',
      'Deine Reservierungen bezahlter Aufgaben, die nicht bestanden haben, und was du dafür geliefert hast.',
      'KI-Guthaben aus Aufgaben, das du noch nicht ausgegeben hast: Es geht mit dem Konto verloren und lässt sich weder nutzen noch übertragen.',
    ],
    staysLabel: 'Was bleibt',
    stays: [
      'Was du in Chat-Räumen geschrieben hast. Die Nachrichten bleiben, wo du sie gepostet hast; der Tag daneben zeigt auf niemanden mehr, und nichts, was bleibt, kann sagen, dass sie von dir waren.',
      'Der Stand in diesem Browser. Deine Level bleiben hier, abgemeldet, genau wie sie sind.',
      'Ein einmaliger Einladungscode, den du eingelöst hast, bleibt eingelöst.',
      'Arbeit, die du für eine bestandene bezahlte Aufgabe geliefert hast und die uns laut den Bedingungen gehört, und der Nachweis über das KI-Guthaben, das sie gebracht hat, ohne Bezug zu dir.',
    ],
    again: 'Wenn du dich danach wieder anmeldest, mit Google oder derselben E-Mail-Adresse, beginnt ein neues, leeres Konto.',
    field: (phrase) => `Tippe „${phrase}“ zur Bestätigung`,
    phrase: 'löschen',
    confirm: 'Mein Konto löschen',
    deleting: 'Wird gelöscht …',
    entitled: 'Dieses Konto hat für etwas bezahlt, das noch läuft: Zeit im Kurs. Wenn du es löschst, gibst du das auf: Es gibt keine Erstattung und keinen Weg zurück.',
    entitledConfirm: 'Trotzdem löschen',
    failed: 'Das hat nicht geklappt. Dein Konto hat sich nicht geändert. Versuch es gleich noch einmal.',
    toastGone: 'Dein Konto ist gelöscht. Dieser Browser behält den Stand, den er schon hatte.',
  },
  rails: {
    build: 'Statur',
    kit: 'Kleidung',
    hair: 'Kopf',
    eyes: 'Augen',
    face: 'Gesicht',
    mouth: 'Mund',
    acc: 'Accessoire',
    tone: 'Farbton',
    plate: 'Rahmen',
    ...cw.de.rails,
  },
  parts: {
    ...cw.de.parts,
    build: { standard: 'Standard', narrow: 'Schmal', broad: 'Breit' },
    kit: {
      ...cw.de.parts.kit,
      hoodie: 'Hoodie',
      tee: 'T-Shirt',
      shirt: 'Hemd',
      jacket: 'Jacke',
      lanyard: 'Schlüsselband',
      turtleneck: 'Rollkragen',
      flannel: 'Flanell',
      vest: 'Weste',
      overalls: 'Latzhose',
      hivis: 'Warnweste',
      tank: 'Tanktop',
      blouse: 'Bluse',
      wraptop: 'Wickeltop',
      scarf: 'Schal',
    },
    hair: {
      shaved: 'Rasiert',
      buzz: 'Buzzcut',
      crop: 'Kurz',
      curtain: 'Mittelscheitel',
      beanie: 'Mütze',
      cap: 'Cap',
      volume: 'Volumen',
      bun: 'Dutt',
      tail: 'Pferdeschwanz',
      mohawk: 'Irokese',
      hood: 'Kapuze auf',
      bob: 'Bob',
      long: 'Lang',
      braids: 'Zöpfe',
      puffs: 'Puffs',
      pixie: 'Pixie',
      locs: 'Locs',
      wrap: 'Kopftuch',
      hijab: 'Hidschab',
    },
    eyes: {
      scan: 'Scan',
      dot: 'Punkt',
      dim: 'Gedimmt',
      wink: 'Zwinkern',
      visor: 'Visier',
      goggles: 'Schutzbrille',
      wide: 'Weit',
      closed: 'Geschlossen',
      shades: 'Sonnenbrille',
      target: 'Zielerfassung',
    },
    face: {
      none: 'Nichts',
      stubble: 'Stoppeln',
      beard: 'Bart',
      freckles: 'Sommersprossen',
      scar: 'Narbe',
      lashes: 'Wimpern',
      brows: 'Brauen',
      mark: 'Stirnzeichen',
    },
    mouth: { line: 'Strich', flat: 'Flach', smirk: 'Schmunzeln', grin: 'Grinsen', open: 'Offen', tight: 'Zusammengepresst', none: 'Nichts' },
    acc: {
      none: 'Nichts',
      glasses: 'Brille',
      stud: 'Ohrstecker',
      cans: 'Kopfhörer',
      band: 'Stirnband',
      wire: 'In-Ear',
      patch: 'Augenklappe',
      pin: 'Schulteranstecker',
      hoops: 'Creolen',
      drops: 'Hängeohrringe',
      chain: 'Kette',
      clip: 'Haarspange',
    },
    tone: { ...cw.de.parts.tone, standard: 'Standard', bright: 'Hell', soft: 'Sanft', deep: 'Tief' },
    plate: { hairline: 'Haarlinie', corners: 'Ecken', dots: 'Punktraster', lines: 'Scanlinie', lift: 'Aufbruch', nod: 'Bereit-Nicken', spark: 'Signalimpuls' },
  },
};

export const ja: typeof en = {
  open: 'プロフィール',
  title: 'プロフィール',
  back: 'レベル一覧',
  noName: '学習者',
  localOnly: 'このブラウザー',
  localOnlyWhy: '記録とアバターは、このブラウザーに保存されています。ログインすると、どちらも別のブラウザーに引き継がれます。',
  signIn: 'ログイン',
  signingIn: 'ログイン中…',
  teams: {
    label: 'あなたのチーム',
    localOnly: 'チームはアカウントに保存されます。作成するにはログインしてください。',
    none: 'まだどのチームにも入っていません。',
    noneWhy: 'チャットパネルを開いて作成してください。招待は、ルームにいる相手のカードから行えます。',
    open: 'ルームを開く',
    waiting: 'チャットパネルに招待が届いています。',
  },
  publicPage: {
    label: '公開ページ',
    what: 'このリンクを知っている人なら誰でも開けます。表示されるのはサーバーが確認したクリアだけで、ブラウザーに保存された記録が表示されることはありません。',
    open: '開く',
    copy: 'リンクをコピー',
    copied: 'リンクをコピーしました',
    localOnly: '他の人が開けるページを持つには、ログインしてください。',
  },
  name: {
    label: '名前',
    edit: '名前を変更',
    close: 'キャンセル',
    field: '名前',
    note: 'ルームでアバターの横に表示される名前で、他の人が開けるページにも載ります。',
    save: '保存',
    saving: '保存中…',
    saved: '保存しました。参加しているすべてのルームに反映されました。',
    failed: 'アカウントに保存できませんでした。名前は変更されていません。少し待ってから、もう一度お試しください。',
    google: 'Googleの名前を使う',
    googleWhy: 'アカウントの名前は裏に残ります。もう一度ログインしても、選んだ名前が上書きされることはありません。',
    keptWhy: 'もう一度ログインしても、選んだ名前が上書きされることはありません。',
    localOnly: 'この名前はこのブラウザーに保存されています。ログインすると、別のブラウザーにも引き継がれます。',
  },
  operator: {
    label: 'あなたのアバター',
    what: '9つの数値から、ブラウザー上で16ピクセル四方に描かれます。',
    where: 'ルームで、あなたの名前の横に表示されます。',
    seed: 'シード',
    seedWhy: 'アバターのすべてです。何もアップロードされません。',
    derived: 'IDから自動生成',
    derivedWhy: '誰でも、最初は誰かの姿から始まります。好きなときに、自分だけのアバターを作れます。',
    edit: '変更する',
    close: '完了',
    picture: 'アカウントの画像',
    pictureWhy: 'アバターを作ると、チャットのあらゆる場所で、この画像の代わりに表示されます。',
  },
  build: {
    title: 'アバターを作る',
    preview: 'プレビュー',
    atSize: (px: number) => `${num(px, 'ja')} px`,
    random: 'おまかせ',
    reset: 'IDから決まるものに戻す',
    save: '着る',
    saving: '保存中…',
    cancel: 'キャンセル',
    unsaved: 'まだ保存されていません',
    saved: '保存しました。参加しているすべてのルームに反映されました。',
    failed: 'アカウントに保存できませんでした。このブラウザーには保存されていて、次の保存でもう一度試します。',
    railLabel: (rail: string) => `${rail}を選択`,
    locked: (level: number) => `${num(level, 'ja')}レベルをクリア`,
    lockedShort: 'ロック中',
    locksWhy: '一部のパーツは、レベルをクリアすると使えるようになります。人らしく見せるためのパーツがロックされることはありません。',
    openParts: (open: number, all: number) => `${num(all, 'ja')}個中${num(open, 'ja')}個のパーツが解放済み`,
  },
  stats: {
    label: '記録',
    rank: 'ランク',
    rankMeter: '次のランクまでの進捗',
    toNextRank: (xp: number, rank: string) => `${rank}まであと${num(xp, 'ja')} XP`,
    topRank: 'ランクの頂点です。',
    xp: 'XP',
    levels: 'レベル',
    stars: '星',
    perfect: '星3つのレベル',
    noHints: 'ヒントなしでクリア',
    hints: '使ったヒント',
    attempts: '挑戦回数',
    unverified: '未検証のクリア',
    unverifiedWhy: '採点が保証できなかったクリアです。もう一度クリーンにプレイすると、星が埋まります。',
    skipped: 'スキップしたレベル',
    skippedWhy: 'クリアせずに先へ進んだレベルです。いつでも戻ってこられます。',
    started: '初クリア',
    latest: '最新のクリア',
    nothingYet: 'まだクリアしたレベルはありません。記録はレベル00から始まります。',
    start: 'レベル00を開始',
  },
  worlds: {
    label: '章別',
    chapter: '章',
    cleared: 'クリア',
    stars: '星',
    locked: '未到達',
  },

  deletion: {
    label: 'アカウントの削除',
    what: 'アカウントを削除すると、保存されているすべてのデータとともにサーバーから削除されます。元に戻すことはできません。',
    localOnly: '削除するアカウントがありません。記録はこのブラウザーに保存されています。',
    open: 'アカウントを削除',
    cancel: 'アカウントを残す',
    title: 'アカウントを削除しますか？',
    goesLabel: '削除されるもの',
    goes: [
      'サーバー上の進捗(レベル、星、XP)。',
      'サーバーが確認したすべてのクリアと、確認のためにアップロードした作業内容。',
      '名前、アバター、他の人が開けるページ。',
      'チャットに投稿した画像すべて。',
      'あなたのチーム。あなたが作ったチームは解散し、そのルームも一緒に削除されます。',
      '合格しなかった有償タスクの予約と、そのために提出したもの。',
      'タスクで得たAIクレジットのうち、まだ使っていない分。アカウントとともに失われ、使うことも移すこともできなくなります。',
    ],
    staysLabel: '残るもの',
    stays: [
      'チャットルームに書いた内容。メッセージは投稿した場所に残りますが、横のタグは誰も指さなくなり、残ったものからあなたの投稿だと判断することはできなくなります。',
      'このブラウザーの記録。レベルはログアウトした状態のまま、ここにそのまま残ります。',
      '使用済みの1回限りの招待コードは、使用済みのままです。',
      '合格した有償タスクのために提出した作業（規約により私たちのものになります）と、それで得たAIクレジットの記録。どちらもあなたとの結び付きは外されます。',
    ],
    again: 'その後、Googleまたは同じメールアドレスでログインすると、新しい空のアカウントが始まります。',
    field: (phrase: string) => `確認のため「${phrase}」と入力してください`,
    phrase: '削除',
    confirm: 'アカウントを削除する',
    deleting: '削除中…',
    entitled: 'このアカウントでは、まだ有効なもの(コースの利用期間)に支払いが行われています。削除するとそれを手放すことになり、返金はできず、取り戻す方法もありません。',
    entitledConfirm: 'それでも削除する',
    failed: '実行できませんでした。アカウントは変更されていません。少し待ってから、もう一度お試しください。',
    toastGone: 'アカウントを削除しました。このブラウザーには、すでにあった記録が残ります。',
  },
  rails: {
    build: '体型',
    kit: '服装',
    hair: '頭',
    eyes: '目',
    face: '顔',
    mouth: '口',
    acc: 'アクセサリー',
    tone: 'インクの色調',
    plate: 'フレーム',
    ...cw.ja.rails,
  },
  parts: {
    ...cw.ja.parts,
    build: { standard: '標準', narrow: '細め', broad: 'がっしり' },
    kit: {
      ...cw.ja.parts.kit,
      hoodie: 'パーカー',
      tee: 'Tシャツ',
      shirt: 'シャツ',
      jacket: 'ジャケット',
      lanyard: 'ネックストラップ',
      turtleneck: 'タートルネック',
      flannel: 'フランネル',
      vest: 'ベスト',
      overalls: 'オーバーオール',
      hivis: '反射ベスト',
      tank: 'タンクトップ',
      blouse: 'ブラウス',
      wraptop: 'カシュクール',
      scarf: 'スカーフ',
    },
    hair: {
      shaved: 'スキンヘッド',
      buzz: 'バズカット',
      crop: 'クロップ',
      curtain: 'センターパート',
      beanie: 'ニット帽',
      cap: 'キャップ',
      volume: 'ボリューム',
      bun: 'お団子',
      tail: 'ポニーテール',
      mohawk: 'モヒカン',
      hood: 'フードをかぶる',
      bob: 'ボブ',
      long: 'ロング',
      braids: '編み込み',
      puffs: 'パフ',
      pixie: 'ピクシー',
      locs: 'ロックス',
      wrap: 'ヘッドラップ',
      hijab: 'ヒジャブ',
    },
    eyes: {
      scan: 'スキャン',
      dot: 'ドット',
      dim: '薄暗い',
      wink: 'ウインク',
      visor: 'バイザー',
      goggles: 'ゴーグル',
      wide: '見開き',
      closed: '閉じる',
      shades: 'サングラス',
      target: 'ターゲティング',
    },
    face: {
      none: 'なし',
      stubble: '無精ひげ',
      beard: 'あごひげ',
      freckles: 'そばかす',
      scar: '傷跡',
      lashes: 'まつげ',
      brows: '眉',
      mark: '額のマーク',
    },
    mouth: { line: '一文字', flat: '平ら', smirk: 'ニヤリ', grin: 'にっこり', open: '開く', tight: '結ぶ', none: 'なし' },
    acc: {
      none: 'なし',
      glasses: 'メガネ',
      stud: 'ピアス',
      cans: 'ヘッドホン',
      band: 'ヘッドバンド',
      wire: 'イヤホン',
      patch: '眼帯',
      pin: '肩のピン',
      hoops: 'フープピアス',
      drops: 'ドロップピアス',
      chain: 'チェーン',
      clip: 'ヘアクリップ',
    },
    tone: { ...cw.ja.parts.tone, standard: '標準', bright: '明るい', soft: 'やわらか', deep: '深い' },
    plate: { hairline: 'ヘアライン', corners: '角', dots: 'ドットグリッド', lines: 'スキャンライン', lift: 'パワーアップ', nod: '準備のうなずき', spark: 'シグナルバースト' },
  },
};
